import type {
  CompanyContact,
  CompanyShellPostalAddress,
  CompanyShellRow,
  PayFrequencySchedule,
} from "../../generated/core-api";
import { splitCsvLine } from "../../shared";
import type {
  CompanyShellCsvColumn,
  CompanyShellCsvParseError,
  CompanyShellCsvParseResult,
  CompanyShellCsvRequirednessRule,
} from "../types";

const PAY_FREQUENCIES: {
  frequency: string;
  firstPpeHeader: string;
  startHeader: string;
}[] = [
  {
    frequency: "weekly",
    firstPpeHeader: "First PPE Date (Weekly)",
    startHeader: "Pay Group Start Date (Weekly)",
  },
  {
    frequency: "fortnightly",
    firstPpeHeader: "First PPE Date (Fortnightly)",
    startHeader: "Pay Group Start Date (Fortnightly)",
  },
  {
    frequency: "monthly",
    firstPpeHeader: "First PPE Date (Monthly)",
    startHeader: "Pay Group Start Date (Monthly)",
  },
];

type HeaderIndex = Record<string, number>;

function buildHeaderIndex(headers: string[]): HeaderIndex {
  const index: HeaderIndex = {};
  headers.forEach((header, i) => {
    index[header.trim().toLowerCase()] = i;
  });
  return index;
}

function cell(values: string[], index: HeaderIndex, header: string): string {
  const i = index[header.toLowerCase()];
  return i === undefined ? "" : (values[i] ?? "").trim();
}

function parseNumber(raw: string): number | undefined {
  if (!raw) return undefined;
  const value = Number(raw);
  return Number.isFinite(value) ? value : undefined;
}

/**
 * Static requiredness legend for each known column, keyed by lowercase
 * header. The uploaded CSV itself is a **single-header** file (row 1 =
 * column names, row 2+ = data) — there is no legend row in the file a PS
 * user actually uploads. This table is instead hand-derived, once, from the
 * reference template (`christmas-default-company-config-copy
 * .shell-upload-template.csv`) that defines the column contract, mirroring
 * how the backend's OpenAPI schema encodes the same rules as field
 * descriptions (Pacific.Agentic.Api#834) — both sides consume the same
 * fixed rules, neither reads them from the uploaded file.
 *
 * A header not in this table is an undeclared column (AC-UPLOAD-12 /
 * R-UPLOAD-2): {@link parseCompanyShellCsv} raises a blocking parse error
 * and the upload/review flow must not treat it as optional. The reference
 * template marks a few columns "To be confirmed" (their eventual home is
 * still unresolved) — those are treated as plain `"optional"` here too;
 * there is no separate not-yet-decided category.
 */
const COLUMN_REQUIREDNESS: Record<
  string,
  { rule: CompanyShellCsvRequirednessRule; legend: string }
> = {
  "company code": { rule: "required", legend: "Required" },
  "company name": { rule: "required", legend: "Required" },
  "company ird no.": { rule: "required", legend: "Required" },
  "datacom start date": { rule: "required", legend: "Required" },
  "company group": { rule: "optional", legend: "Optional" },
  "postal address line 1": { rule: "required", legend: "Required" },
  "postal address line 2": { rule: "required", legend: "Required" },
  "postal address line 3": { rule: "optional", legend: "Optional" },
  "main contact": { rule: "required", legend: "Required" },
  "position (main contact)": { rule: "required", legend: "Required" },
  "phone (main contact)": { rule: "required", legend: "Required" },
  "efiling option": { rule: "required", legend: "Required" },
  "large company": { rule: "required", legend: "Required" },
  "company region": { rule: "required", legend: "Required" },
  delivery: { rule: "required", legend: "Required" },
  "sick leave stand down": { rule: "required", legend: "Required" },
  "acr product type": { rule: "required", legend: "Required" },
  "trading name": { rule: "optional", legend: "Optional" },
  mobile: { rule: "optional", legend: "Optional" },
  "email (main contact)": { rule: "optional", legend: "Optional" },
  "secondary contact": { rule: "optional", legend: "Optional" },
  "position (secondary contact)": {
    rule: "optional_if_secondary_contact",
    legend: "Optional if Secondary Contact",
  },
  "phone (secondary contact)": {
    rule: "optional_if_secondary_contact",
    legend: "Optional if Secondary Contact",
  },
  "email (secondary contact)": { rule: "optional", legend: "Optional" },
  "delivery note": { rule: "optional", legend: "Optional" },
  "fte standard hours per week": { rule: "optional", legend: "Optional" },
  "week start day": { rule: "optional", legend: "Optional" },
  "company annual leave anniversary date": {
    rule: "optional",
    legend: "Optional",
  },
  "kiwisaver option": { rule: "optional", legend: "Optional" },
  "acc classification unit": { rule: "optional", legend: "Optional" },
  "first ppe date (weekly)": {
    rule: "optional_if_weekly_retained",
    legend: "Optional if Weekly retained",
  },
  "pay group start date (weekly)": {
    rule: "optional_if_weekly_retained",
    legend: "Optional if Weekly retained",
  },
  "first ppe date (fortnightly)": {
    rule: "optional_if_fortnightly_retained",
    legend: "Optional if Fortnightly retained",
  },
  "pay group start date (fortnightly)": {
    rule: "optional_if_fortnightly_retained",
    legend: "Optional if Fortnightly retained",
  },
  "first ppe date (monthly)": {
    rule: "optional_if_monthly_retained",
    legend: "Optional if Monthly retained",
  },
  "pay group start date (monthly)": {
    rule: "optional_if_monthly_retained",
    legend: "Optional if Monthly retained",
  },
};

/**
 * The upload columns in template order, spelled as the reference template
 * spells them.
 *
 * Exported so the document-upload path can serialise the Doc2Company agent's
 * keyed JSON into a CSV the rest of the flow accepts.
 *
 * The casing is load-bearing and cannot be derived from
 * {@link COLUMN_REQUIREDNESS}, whose keys are lowercased for case-insensitive
 * parser lookups. Review reads cell values through
 * {@link CSV_HEADER_TO_FIELD_PATH}, which is keyed by these exact spellings and
 * is **not** case-insensitive: a lowercase header still parses into a correct
 * row, so the company name renders, but every field in Review reads blank and
 * the live validator reports every required column as missing.
 * `csvHeadersMatchRequirednessTable` in the tests keeps the two in step.
 */
export const COMPANY_SHELL_CSV_HEADERS: readonly string[] = [
  "Company Code",
  "Company Name",
  "Company IRD No.",
  "Datacom Start Date",
  "Company Group",
  "Postal Address Line 1",
  "Postal Address Line 2",
  "Postal Address Line 3",
  "Main Contact",
  "Position (main contact)",
  "Phone (main contact)",
  "EFiling Option",
  "Large Company",
  "Company Region",
  "Delivery",
  "Sick Leave Stand Down",
  "ACR Product Type",
  "Trading Name",
  "Mobile",
  "Email (main contact)",
  "Secondary Contact",
  "Position (secondary contact)",
  "Phone (secondary contact)",
  "Email (secondary contact)",
  "Delivery Note",
  "FTE Standard Hours per Week",
  "Week Start Day",
  "Company Annual Leave Anniversary Date",
  "KiwiSaver Option",
  "ACC Classification Unit",
  "First PPE Date (Weekly)",
  "Pay Group Start Date (Weekly)",
  "First PPE Date (Fortnightly)",
  "Pay Group Start Date (Fortnightly)",
  "First PPE Date (Monthly)",
  "Pay Group Start Date (Monthly)",
];

/**
 * Whether ``header`` is one of the Appendix C shell-template column names
 * (case-insensitive). Used by Validate/Commit gating so undeclared columns
 * (AC-UPLOAD-12) cannot be treated as Pass.
 *
 * Uses an own-property check — ``in`` would also match
 * ``Object.prototype`` keys like ``constructor`` / ``toString``.
 */
export function isKnownShellCsvHeader(header: string): boolean {
  return Object.prototype.hasOwnProperty.call(
    COLUMN_REQUIREDNESS,
    header.trim().toLowerCase()
  );
}

/**
 * Plain-English blocking message for an undeclared shell CSV column
 * (R-UPLOAD-2 / R-UPLOAD-8).
 */
export function unknownShellCsvColumnMessage(header: string): string {
  return (
    `Unknown column "${header}" — shell CSV must use only the template ` +
    "column headers."
  );
}

/**
 * Cell-error key fragment for an undeclared CSV header. Namespaced so it
 * cannot collide with a backend validate field path (e.g. a rogue header
 * literally named ``company_code``).
 */
export function unknownShellCsvColumnCellKey(header: string): string {
  return `csv-header:${header}`;
}

function columnRequiredness(header: string): {
  rule: CompanyShellCsvRequirednessRule;
  legend: string;
} {
  const key = header.trim().toLowerCase();
  if (!Object.prototype.hasOwnProperty.call(COLUMN_REQUIREDNESS, key)) {
    // Display-only fallback for an undeclared header that is still listed
    // in `columns` so the review grid can show Fail against that line —
    // parseErrors / Validate / Commit gating carry the actual block.
    return { rule: "optional", legend: "" };
  }
  return COLUMN_REQUIREDNESS[key];
}

/**
 * CSV header -> the `CompanyShellRow` field path a validate error's `column`
 * would name for it — confirmed against two real `company_shell_provision`
 * responses to use the row's own field names verbatim (dotted for nested
 * fields, e.g. `main_contact.phone`). Used to anchor a validate error to
 * the company field it's actually about (see `CompanyShellPreview`).
 *
 * Deliberately omits the six pay-schedule headers (`First/Pay Group Start
 * Date (Weekly/Fortnightly/Monthly)`) — `pay_schedules` is a list, and
 * there's no real example yet of what path the backend reports for an
 * error inside it. An error naming one of those (or any other field not
 * listed here) still isn't lost — it falls back to the row-level line.
 */
export const CSV_HEADER_TO_FIELD_PATH: Record<string, string> = {
  "Company Code": "company_code",
  "Company Name": "company_name",
  "Datacom Start Date": "start_date",
  "Company Group": "companyGroupName",
  "Trading Name": "trading_name",
  "Company Region": "region_name",
  "Company IRD No.": "company_ird_no",
  "Company Annual Leave Anniversary Date": "annual_leave_anniversary_date",
  "Sick Leave Stand Down": "sick_leave_stand_down",
  "Main Contact": "main_contact.name",
  "Position (main contact)": "main_contact.position",
  "Phone (main contact)": "main_contact.phone",
  Mobile: "main_contact.mobile",
  "Email (main contact)": "main_contact.email",
  "Secondary Contact": "secondary_contact.name",
  "Position (secondary contact)": "secondary_contact.position",
  "Phone (secondary contact)": "secondary_contact.phone",
  "Email (secondary contact)": "secondary_contact.email",
  "Postal Address Line 1": "postal_address.line1",
  "Postal Address Line 2": "postal_address.line2",
  "Postal Address Line 3": "postal_address.line3",
  Delivery: "delivery",
  "Delivery Note": "delivery_note",
  "EFiling Option": "efiling_option",
  "Large Company": "large_company",
  "ACR Product Type": "acr_product_type",
  "KiwiSaver Option": "kiwisaver_option",
  "ACC Classification Unit": "acc_classification_unit",
  "FTE Standard Hours per Week": "fte_standard_hours_per_week",
  "Week Start Day": "week_start_day",
};

/**
 * Canonical headers that must exist in every upload. Deriving this list from
 * the same requiredness contract used for row values keeps header-level and
 * row-level validation in sync as the template evolves.
 */
const REQUIRED_HEADERS = Object.keys(CSV_HEADER_TO_FIELD_PATH).filter(
  (header) =>
    COLUMN_REQUIREDNESS[header.toLowerCase()]?.rule === "required"
);

/** `"6 Months"` -> `{ amount: 6, period_type: "months" }`. Undefined if unparseable — the server `validate` invoke is authoritative for format errors. */
function parseSickLeaveStandDown(
  raw: string
): CompanyShellRow["sick_leave_stand_down"] {
  const match = raw.trim().match(/^(\d+(?:\.\d+)?)\s+(\S+)$/);
  if (!match) return undefined;
  return { amount: Number(match[1]), period_type: match[2].toLowerCase() };
}

/** A source of field values by CSV header name — a raw CSV row, or an edited display row. */
type CellGetter = (header: string) => string;

function buildAddress(
  get: CellGetter
): CompanyShellPostalAddress | undefined {
  const line1 = get("Postal Address Line 1");
  const line2 = get("Postal Address Line 2");
  const line3 = get("Postal Address Line 3");
  if (!line1 && !line2 && !line3) return undefined;

  const address: CompanyShellPostalAddress = {};
  if (line1) address.line1 = line1;
  if (line2) address.line2 = line2;
  if (line3) address.line3 = line3;
  return address;
}

function buildContact(
  get: CellGetter,
  kind: "main" | "secondary"
): CompanyContact | undefined {
  const nameHeader = kind === "main" ? "Main Contact" : "Secondary Contact";
  const name = get(nameHeader);
  if (!name) return undefined;

  const suffix = kind === "main" ? "main contact" : "secondary contact";
  const contact: CompanyContact = { name };

  const position = get(`Position (${suffix})`);
  if (position) contact.position = position;

  const phone = get(`Phone (${suffix})`);
  if (phone) contact.phone = phone;

  if (kind === "main") {
    const mobile = get("Mobile");
    if (mobile) contact.mobile = mobile;
  }

  const email = get(`Email (${suffix})`);
  if (email) contact.email = email;

  return contact;
}

function buildPaySchedules(get: CellGetter): PayFrequencySchedule[] {
  const schedules: PayFrequencySchedule[] = [];
  for (const { frequency, firstPpeHeader, startHeader } of PAY_FREQUENCIES) {
    const firstPpeDate = get(firstPpeHeader);
    const payGroupStartDate = get(startHeader);
    if (!firstPpeDate && !payGroupStartDate) continue;

    const schedule: PayFrequencySchedule = { frequency };
    if (firstPpeDate) schedule.first_ppe_date = firstPpeDate;
    if (payGroupStartDate) schedule.pay_group_start_date = payGroupStartDate;
    schedules.push(schedule);
  }
  return schedules;
}

/**
 * Build a `CompanyShellRow` from any header-keyed source of field values —
 * a raw CSV data row (`parseCompanyShellCsv`) or an edited display row (the
 * Review screen, after a cell edit) — so the CSV-column → field mapping is
 * maintained in exactly one place. Assumes `company_code`/`company_name`/
 * `start_date` are already known non-blank (callers validate that
 * separately, since it's a structural requirement, not just a mapping step).
 */
export function buildCompanyShellRow(get: CellGetter): CompanyShellRow {
  const row: CompanyShellRow = {
    company_code: get("Company Code"),
    company_name: get("Company Name"),
    start_date: get("Datacom Start Date"),
  };

  const companyGroupName = get("Company Group");
  if (companyGroupName) row.companyGroupName = companyGroupName;

  const tradingName = get("Trading Name");
  if (tradingName) row.trading_name = tradingName;

  const regionName = get("Company Region");
  if (regionName) row.region_name = regionName;

  const irdNo = get("Company IRD No.");
  if (irdNo) row.company_ird_no = irdNo;

  const anniversaryDate = get("Company Annual Leave Anniversary Date");
  if (anniversaryDate) row.annual_leave_anniversary_date = anniversaryDate;

  const sickLeaveStandDown = get("Sick Leave Stand Down");
  if (sickLeaveStandDown) {
    const parsed = parseSickLeaveStandDown(sickLeaveStandDown);
    if (parsed) row.sick_leave_stand_down = parsed;
  }

  const mainContact = buildContact(get, "main");
  if (mainContact) row.main_contact = mainContact;

  const secondaryContact = buildContact(get, "secondary");
  if (secondaryContact) row.secondary_contact = secondaryContact;

  const postalAddress = buildAddress(get);
  if (postalAddress) row.postal_address = postalAddress;

  const delivery = get("Delivery");
  if (delivery) row.delivery = delivery;

  const deliveryNote = get("Delivery Note");
  if (deliveryNote) row.delivery_note = deliveryNote;

  const paySchedules = buildPaySchedules(get);
  if (paySchedules.length > 0) row.pay_schedules = paySchedules;

  const efilingOption = get("EFiling Option");
  if (efilingOption) row.efiling_option = efilingOption;

  const largeCompany = get("Large Company");
  if (largeCompany) row.large_company = largeCompany;

  const acrProductType = get("ACR Product Type");
  if (acrProductType) row.acr_product_type = acrProductType;

  const kiwisaverOption = get("KiwiSaver Option");
  if (kiwisaverOption) row.kiwisaver_option = kiwisaverOption;

  const accClassificationUnit = get("ACC Classification Unit");
  if (accClassificationUnit) row.acc_classification_unit = accClassificationUnit;

  const fteStandardHours = get("FTE Standard Hours per Week");
  if (fteStandardHours) {
    const parsed = parseNumber(fteStandardHours);
    if (parsed !== undefined) row.fte_standard_hours_per_week = parsed;
  }

  const weekStartDay = get("Week Start Day");
  if (weekStartDay) row.week_start_day = weekStartDay;

  return row;
}

/**
 * Parse a company-shell upload CSV (single header row of column names, per
 * the column contract in `christmas-default-company-config-copy
 * .shell-upload-template.csv`) into `CompanyShellRow[]`.
 *
 * This is a structural mapper — it groups CSV columns into the nested
 * `CompanyShellRow` shape (addresses, contacts, pay schedules) and does light
 * type coercion (numbers, the sick-leave-standdown split) — plus
 * enforcement of the static {@link COLUMN_REQUIREDNESS} table's
 * unconditional `"required"` columns: every one is checked per row (a blank
 * cell is a parse error), so one blank required cell never hides another
 * blank required cell in the same row. Rows with blank required values are
 * still included in `rows` so they can be repaired during Review; generated
 * `CompanyShellRow` identity fields remain strings and use `""` until fixed.
 * Rows with fewer values than the header are retained: CSV fields map
 * positionally, so absent trailing values can be treated as blank and fixed
 * during Review. Rows with extra values are excluded because those values
 * cannot be mapped to a header safely. It also flags a duplicate Company
 * Code within this CSV (C.1 row 1) — a check
 * scoped entirely to the uploaded file, unlike the rest of row 1's checks
 * (format, cross-environment uniqueness), which stay server-side. It does
 * **not** enforce the *conditional* requiredness rules (e.g. Region required
 * for AU, a pay schedule required per retained frequency) — those need
 * cross-field/cross-row context this pass doesn't have, so they're validated
 * server-side via the `validate` agent invocation, which is the source of
 * truth for them (see Pacific.Agentic.Api#834).
 */
export function parseCompanyShellCsv(
  raw: string
): CompanyShellCsvParseResult {
  const lines = raw.replace(/\r\n/g, "\n").replace(/\r/g, "\n").split("\n");

  const headerLine = lines[0];
  if (headerLine === undefined || headerLine.trim() === "") {
    return {
      columns: [],
      rows: [],
      rowCsvLineNumbers: [],
      parseErrors: [{ message: "The CSV file is empty." }],
    };
  }

  const headers = splitCsvLine(headerLine).map((h) => h.trim());

  const columns: CompanyShellCsvColumn[] = headers.map((header) => {
    const { rule, legend } = columnRequiredness(header);
    return { header, legend, requiredness: rule };
  });

  const parseErrors: CompanyShellCsvParseError[] = [];
  const lowerHeaders = headers.map((h) => h.toLowerCase());
  for (const required of REQUIRED_HEADERS) {
    if (!lowerHeaders.includes(required.toLowerCase())) {
      parseErrors.push({
        column: required,
        message: `Missing required column "${required}".`,
      });
    }
  }

  // Undeclared headers (not in Appendix C / COLUMN_REQUIREDNESS) block
  // validation per R-UPLOAD-2 / AC-UPLOAD-12 — including stale legacy
  // template columns that were removed from the contract.
  for (const header of headers) {
    if (!isKnownShellCsvHeader(header)) {
      parseErrors.push({
        column: header,
        message: unknownShellCsvColumnMessage(header),
      });
    }
  }

  // Deliberately does NOT return early when a required header is missing —
  // rows are still parsed (any column absent from the header just reads as
  // blank via `cell()`, and is naturally skipped by the requiredness loop
  // below since it isn't in `columns`), so a missing header never hides
  // other row-level issues, like a blank required cell in a column that
  // *does* exist.

  const index = buildHeaderIndex(headers);
  const rows: CompanyShellRow[] = [];
  const rowCsvLineNumbers: number[] = [];
  // First 1-based data row each Company Code was seen on — dedup is
  // scoped to this CSV only (C.1 row 1); cross-environment uniqueness is
  // checked server-side, where the existing company set is known. Matching
  // is case-insensitive (keyed on a trimmed, lowercased Company Code) to
  // mirror the backend's `_validate_company_code_uniqueness_within_batch`
  // (Pacific.Agentic.Api#954), which trims + lowercases before comparing —
  // e.g. "DUP01" and "dup01" ARE flagged as duplicates of each other. The
  // original-cased value is kept for the error message text.
  const firstLineByCompanyCode = new Map<string, number>();

  // Data starts at line index 1 — line 0 is the (single) header row.
  for (let i = 1; i < lines.length; i++) {
    const line = lines[i];
    if (line.trim() === "") continue;

    const rowIndex = i; // 1-based data row number
    const values = splitCsvLine(line);

    const hasMissingTrailingColumns = values.length < headers.length;
    const hasExtraColumns = values.length > headers.length;
    if (hasMissingTrailingColumns) {
      parseErrors.push({
        code: "missing_trailing_columns",
        csv_line_number: rowIndex,
        message:
          `Row has ${values.length} column(s), expected ${headers.length}. ` +
          "Missing trailing fields were treated as blank.",
      });
    } else if (hasExtraColumns) {
      parseErrors.push({
        code: "extra_columns",
        csv_line_number: rowIndex,
        message:
          `Row has ${values.length} column(s), expected ${headers.length}. ` +
          "Extra fields cannot be matched to columns.",
      });
      // Deliberately does NOT `continue` here — `cell()` degrades gracefully
      // (missing positions read as blank and extra positions are ignored), so
      // the required-column check below still surfaces everything wrong with
      // this row in one pass.
    }

    // Check every unconditional "Required" column up front — including the
    // three structural fields — so a blank cell never hides other blank
    // required cells in the same row behind a single combined message.
    for (const column of columns) {
      if (column.requiredness !== "required") continue;

      const value = cell(values, index, column.header);
      if (!value) {
        parseErrors.push({
          csv_line_number: rowIndex,
          column: column.header,
          message: `"${column.header}" is required.`,
        });
      }
    }

    const companyCode = cell(values, index, "Company Code");

    if (companyCode) {
      const dedupKey = companyCode.trim().toLowerCase();
      const firstLine = firstLineByCompanyCode.get(dedupKey);
      if (firstLine === undefined) {
        firstLineByCompanyCode.set(dedupKey, rowIndex);
      } else {
        parseErrors.push({
          csv_line_number: rowIndex,
          column: "Company Code",
          message: `Duplicate "Company Code" "${companyCode}" — already used on row ${firstLine}.`,
        });
      }
    }

    if (hasExtraColumns) {
      // The mismatch is reported above. Extra values cannot be associated
      // with a header safely, so this row must be corrected in the CSV.
      continue;
    }

    const row = buildCompanyShellRow((header) => cell(values, index, header));
    rows.push(row);
    rowCsvLineNumbers.push(rowIndex);
  }

  return { columns, rows, rowCsvLineNumbers, parseErrors };
}

/**
 * Whether a parse problem must be corrected in the CSV before Review.
 * Field-specific row issues can be anchored to an editable Review control.
 * A short row is also recoverable because its missing trailing values become
 * editable blanks; file/header issues and rows with extra values cannot.
 */
export function isBlockingCompanyShellParseError(
  error: CompanyShellCsvParseError
): boolean {
  if (error.code === "missing_trailing_columns") return false;
  return error.csv_line_number === undefined || error.column === undefined;
}

/** A recoverable CSV shape note that does not represent active company data. */
export function isCompanyShellImportNote(
  error: CompanyShellCsvParseError
): boolean {
  return error.code === "missing_trailing_columns";
}

/**
 * Format a parsed `CompanyShellRow` back into a flat `{ header: displayValue }`
 * map, keyed by the same CSV header names as {@link parseCompanyShellCsv}'s
 * `columns` — used to render the preview table row-by-row so it proves the
 * structural mapping worked (e.g. `main_contact.email`, pulled from a
 * non-adjacent column) instead of just echoing the raw CSV text back.
 */
export function formatCompanyShellRowForPreview(
  row: CompanyShellRow
): Record<string, string> {
  const weekly = row.pay_schedules?.find((p) => p.frequency === "weekly");
  const fortnightly = row.pay_schedules?.find(
    (p) => p.frequency === "fortnightly"
  );
  const monthly = row.pay_schedules?.find((p) => p.frequency === "monthly");

  return {
    "Company Code": row.company_code,
    "Company Name": row.company_name,
    "Company IRD No.": row.company_ird_no ?? "",
    "Datacom Start Date": row.start_date,
    "Company Group": row.companyGroupName ?? "",
    "Postal Address Line 1": row.postal_address?.line1 ?? "",
    "Postal Address Line 2": row.postal_address?.line2 ?? "",
    "Postal Address Line 3": row.postal_address?.line3 ?? "",
    "Main Contact": row.main_contact?.name ?? "",
    "Position (main contact)": row.main_contact?.position ?? "",
    "Phone (main contact)": row.main_contact?.phone ?? "",
    "EFiling Option": row.efiling_option ?? "",
    "Large Company": row.large_company ?? "",
    "Company Region": row.region_name ?? "",
    Delivery: row.delivery ?? "",
    "Sick Leave Stand Down": row.sick_leave_stand_down
      ? `${row.sick_leave_stand_down.amount} ${row.sick_leave_stand_down.period_type}`
      : "",
    "ACR Product Type": row.acr_product_type ?? "",
    "Trading Name": row.trading_name ?? "",
    Mobile: row.main_contact?.mobile ?? "",
    "Email (main contact)": row.main_contact?.email ?? "",
    "Secondary Contact": row.secondary_contact?.name ?? "",
    "Position (secondary contact)": row.secondary_contact?.position ?? "",
    "Phone (secondary contact)": row.secondary_contact?.phone ?? "",
    "Email (secondary contact)": row.secondary_contact?.email ?? "",
    "Delivery Note": row.delivery_note ?? "",
    "FTE Standard Hours per Week":
      row.fte_standard_hours_per_week?.toString() ?? "",
    "Week Start Day": row.week_start_day ?? "",
    "Company Annual Leave Anniversary Date":
      row.annual_leave_anniversary_date ?? "",
    "KiwiSaver Option": row.kiwisaver_option ?? "",
    "ACC Classification Unit": row.acc_classification_unit ?? "",
    "First PPE Date (Weekly)": weekly?.first_ppe_date ?? "",
    "Pay Group Start Date (Weekly)": weekly?.pay_group_start_date ?? "",
    "First PPE Date (Fortnightly)": fortnightly?.first_ppe_date ?? "",
    "Pay Group Start Date (Fortnightly)":
      fortnightly?.pay_group_start_date ?? "",
    "First PPE Date (Monthly)": monthly?.first_ppe_date ?? "",
    "Pay Group Start Date (Monthly)": monthly?.pay_group_start_date ?? "",
  };
}
